/**
 * Caregiver Dashboard
 *
 * Dashboard for caregivers showing:
 * - Today's schedule with map/directions
 * - Pending tasks by client
 * - Timesheet status (submitted, approved, paid)
 * - Training/credential expiration reminders
 * 
 * Error Recovery Features:
 * - Connection status monitoring with visual indicator
 * - Data freshness indicators showing "last updated" timestamps
 * - Manual refresh buttons on each widget
 * - LocalStorage fallback for offline access to last known data
 * - Stale data warnings (>5 minutes old)
 * - Optimistic UI updates for check-in/check-out actions
 * 
 * Regulatory Context:
 * - Supports audit requirements by showing data freshness
 * - Critical for EVV compliance when connectivity is intermittent
 * - Offline-first design ensures caregivers can access schedules
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth, useIsMobile } from '@/core/hooks';
import { useConnectionStatus } from '@/hooks';
import { Card, CardHeader, CardContent, Button, EmptyState } from '@/core/components';
import { DataFreshness, StaleDataWarning } from '@/components/DataFreshness';
import { NetworkStatusBanner } from '@/components/sync/NetworkStatusBanner';
import {
  getLocalStorage,
  setLocalStorage,
  getLastUpdated,
  CACHE_KEYS,
  CACHE_EXPIRATION,
} from '@/utils/localStorage';
import {
  publishLiveCarerStatus,
  getStoredVisitVerificationRecords,
  upsertStoredVisitVerificationRecord,
  type LiveCarerTrackingRecord,
  type GeofenceStatus,
  type VisitAuditEvent,
  type VisitScheduleStatus,
  type VisitTaskEvidence,
  type VisitVerificationRecord,
} from '@/utils/liveTelemetryApi';
import {
  Calendar,
  Clock,
  MapPin,
  Navigation,
  CheckCircle,
  AlertCircle,
  Award,
  FileText,
  AlertTriangle,
  Shield,
  Users,
  Phone,
  Wifi,
  WifiOff,
  ClipboardCheck,
  MessageSquareWarning,
  Send,
  Database,
  Mic,
  Sparkles,
  Wand2,
  ClipboardList,
} from 'lucide-react';

interface VisitCardProps {
  id: string;
  clientName: string;
  visitType: string;
  scheduledDate: string;
  time: string;
  duration: string;
  address: string;
  clientPhone?: string;
  status: 'upcoming' | 'in-progress' | 'completed';
  tasks: string[];
  checkInAt?: string;
  checkOutAt?: string;
  verificationStatus?: 'scheduled' | 'checked-in' | 'payroll-locked';
  geofenceStatus?: GeofenceStatus;
  distanceFromClientMeters?: number;
  gpsAccuracyMeters?: number;
  scheduleStatus?: VisitScheduleStatus;
  verificationExceptions?: string[];
  onNavigate?: () => void;
  onCheckIn?: () => Promise<void>;
  onCheckOut?: () => Promise<void>;
  onViewDetails?: () => void;
  onCall?: () => void;
  isOptimistic?: boolean;
}

const VisitCard: React.FC<VisitCardProps> = ({
  clientName,
  visitType,
  scheduledDate,
  time,
  duration,
  address,
  clientPhone,
  status,
  tasks,
  checkInAt,
  checkOutAt,
  verificationStatus = 'scheduled',
  geofenceStatus,
  distanceFromClientMeters,
  gpsAccuracyMeters,
  scheduleStatus,
  verificationExceptions = [],
  onNavigate,
  onCheckIn,
  onCheckOut,
  onViewDetails,
  onCall,
  isOptimistic = false,
}) => {
  const isMobile = useIsMobile();
  const [isProcessing, setIsProcessing] = useState(false);

  const getStatusStyles = () => {
    switch (status) {
      case 'upcoming':
        return {
          border: 'border-blue-200',
          bg: 'bg-blue-50',
          badge: 'bg-blue-500',
          text: 'Upcoming',
        };
      case 'in-progress':
        return {
          border: 'border-green-200',
          bg: 'bg-green-50',
          badge: 'bg-green-500',
          text: 'In Progress',
        };
      case 'completed':
        return {
          border: 'border-gray-200',
          bg: 'bg-gray-50',
          badge: 'bg-gray-500',
          text: 'Completed',
        };
    }
  };

  const styles = getStatusStyles();

  const handleCheckIn = async () => {
    if (!onCheckIn || isProcessing) return;
    setIsProcessing(true);
    try {
      await onCheckIn();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCheckOut = async () => {
    if (!onCheckOut || isProcessing) return;
    setIsProcessing(true);
    try {
      await onCheckOut();
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Card padding="md" className={`border-2 ${styles.border} ${styles.bg} ${isOptimistic ? 'opacity-70' : ''}`}>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-gray-900">{clientName}</h3>
              <span className={`px-2 py-0.5 text-xs font-bold text-white rounded ${styles.badge}`}>
                {styles.text}
              </span>
              {isOptimistic && (
                <span className="px-2 py-0.5 text-xs font-medium text-gray-600 bg-gray-200 rounded">
                  Syncing...
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600 mt-1">{visitType}</p>
            <p className="mt-1 text-xs font-medium text-emerald-700">
              Payroll verification: {verificationStatus.replace(/-/g, ' ')}
            </p>
          </div>
        </div>

        {/* Time and Duration */}
        <div className="flex items-center gap-4 text-sm text-gray-700">
          <div className="flex items-center gap-1">
            <Clock className="h-4 w-4" />
            {scheduledDate === todayIso() ? 'Today' : formatDate(scheduledDate)} - {time}
          </div>
          <div className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            {duration}
          </div>
        </div>

        {/* Address */}
        <div className="flex items-start gap-2 text-sm text-gray-700">
          <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{address}</span>
        </div>

        {/* Client Phone (Mobile) */}
        {clientPhone && isMobile && onCall && (
          <div className="flex items-center gap-2">
            <Phone className="h-4 w-4 text-gray-400" />
            <button
              onClick={onCall}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium underline"
            >
              {clientPhone}
            </button>
          </div>
        )}

        {checkInAt && (
          <div className="rounded-md bg-white/70 p-3 text-sm text-slate-700">
            <p>Check-in: {new Date(checkInAt).toLocaleString('en-GB')}</p>
            {checkOutAt && <p>Check-out: {new Date(checkOutAt).toLocaleString('en-GB')}</p>}
            {geofenceStatus && (
              <p>
                GPS verification: {geofenceStatus}
                {typeof distanceFromClientMeters === 'number' ? `, ${Math.round(distanceFromClientMeters)}m from client` : ''}
                {typeof gpsAccuracyMeters === 'number' ? `, accuracy ${Math.round(gpsAccuracyMeters)}m` : ''}
              </p>
            )}
            {scheduleStatus && <p>Schedule status: {scheduleStatus.replace(/-/g, ' ')}</p>}
            {verificationExceptions.length > 0 && (
              <p className="mt-1 font-semibold text-amber-800">
                Review: {verificationExceptions.join('; ')}
              </p>
            )}
          </div>
        )}

        {/* Tasks */}
        {tasks.length > 0 && (
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Tasks:</p>
            <ul className="space-y-1">
              {tasks.map((task, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-gray-400" />
                  {task}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Actions */}
        <div className={`flex ${isMobile ? 'flex-col' : 'flex-wrap'} gap-2 pt-2 border-t border-gray-200`}>
          {onCheckIn && status === 'upcoming' && (
            <Button
              variant="primary"
              size={isMobile ? 'lg' : 'sm'}
              onClick={() => void handleCheckIn()}
              disabled={isProcessing}
              className={isMobile ? 'w-full min-h-[44px] text-base font-semibold' : ''}
            >
              <CheckCircle className={isMobile ? 'h-5 w-5' : 'h-4 w-4'} />
              {isProcessing ? 'Checking In...' : 'Check In'}
            </Button>
          )}
          {onCheckOut && status === 'in-progress' && (
            <Button
              variant="primary"
              size={isMobile ? 'lg' : 'sm'}
              onClick={() => void handleCheckOut()}
              disabled={isProcessing}
              className={isMobile ? 'w-full min-h-[44px] text-base font-semibold' : ''}
            >
              <CheckCircle className={isMobile ? 'h-5 w-5' : 'h-4 w-4'} />
              {isProcessing ? 'Checking Out...' : 'Check Out'}
            </Button>
          )}
          <div className={`flex ${isMobile ? 'flex-col' : 'flex-wrap'} gap-2 ${isMobile ? 'w-full' : ''}`}>
            {onNavigate && (
              <Button
                variant="outline"
                size={isMobile ? 'lg' : 'sm'}
                onClick={onNavigate}
                className={`flex items-center gap-1 ${isMobile ? 'w-full min-h-[44px]' : ''}`}
              >
                <Navigation className={isMobile ? 'h-5 w-5' : 'h-4 w-4'} />
                Directions
              </Button>
            )}
            {onCall && clientPhone && isMobile && (
              <Button
                variant="outline"
                size="lg"
                onClick={onCall}
                className="w-full min-h-[44px] flex items-center gap-2"
              >
                <Phone className="h-5 w-5" />
                Call Client
              </Button>
            )}
            {onCall && clientPhone && !isMobile && (
              <Button
                variant="outline"
                size="sm"
                onClick={onCall}
                className="flex items-center gap-2"
              >
                <Phone className="h-4 w-4" />
                Call Client
              </Button>
            )}
            {onViewDetails && (
              <Button
                variant="outline"
                size={isMobile ? 'lg' : 'sm'}
                onClick={onViewDetails}
                className={isMobile ? 'w-full min-h-[44px]' : ''}
              >
                View Details
              </Button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

interface TimesheetStatusProps {
  period: string;
  hours: number;
  status: 'draft' | 'submitted' | 'approved' | 'paid';
  onClick?: () => void;
}

const TimesheetStatus: React.FC<TimesheetStatusProps> = ({ period, hours, status, onClick }) => {
  const getStatusStyles = () => {
    switch (status) {
      case 'draft':
        return {
          bg: 'bg-gray-100',
          text: 'text-gray-700',
          badge: 'bg-gray-500',
          label: 'Draft',
        };
      case 'submitted':
        return {
          bg: 'bg-blue-100',
          text: 'text-blue-700',
          badge: 'bg-blue-500',
          label: 'Submitted',
        };
      case 'approved':
        return {
          bg: 'bg-green-100',
          text: 'text-green-700',
          badge: 'bg-green-500',
          label: 'Approved',
        };
      case 'paid':
        return {
          bg: 'bg-purple-100',
          text: 'text-purple-700',
          badge: 'bg-purple-500',
          label: 'Paid',
        };
    }
  };

  const styles = getStatusStyles();

  return (
    <div
      className={`p-4 rounded-lg ${styles.bg} ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="font-semibold text-gray-900">{period}</p>
          <p className={`text-sm mt-1 ${styles.text}`}>{hours} hours</p>
        </div>
        <span className={`px-3 py-1 text-xs font-bold text-white rounded ${styles.badge}`}>
          {styles.label}
        </span>
      </div>
    </div>
  );
};

interface CredentialAlertProps {
  title: string;
  expiresIn: string;
  severity: 'critical' | 'warning' | 'info';
  onClick?: () => void;
}

const CredentialAlert: React.FC<CredentialAlertProps> = ({ title, expiresIn, severity, onClick }) => {
  const getSeverityStyles = () => {
    switch (severity) {
      case 'critical':
        return {
          bg: 'bg-red-50',
          border: 'border-red-200',
          icon: 'text-red-600',
        };
      case 'warning':
        return {
          bg: 'bg-yellow-50',
          border: 'border-yellow-200',
          icon: 'text-yellow-600',
        };
      case 'info':
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          icon: 'text-blue-600',
        };
    }
  };

  const styles = getSeverityStyles();

  return (
    <div
      className={`p-3 rounded-lg border ${styles.bg} ${styles.border} ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-start gap-2">
        <AlertCircle className={`h-5 w-5 ${styles.icon} mt-0.5`} />
        <div className="flex-1">
          <h4 className="font-semibold text-gray-900 text-sm">{title}</h4>
          <p className="text-sm text-gray-600 mt-0.5">Expires in {expiresIn}</p>
        </div>
      </div>
    </div>
  );
};

// Types for dashboard data
interface VisitData {
  id: string;
  clientName: string;
  visitType: string;
  scheduledDate: string;
  time: string;
  duration: string;
  address: string;
  clientPhone?: string;
  status: 'upcoming' | 'in-progress' | 'completed';
  tasks: string[];
  checkInAt?: string;
  checkOutAt?: string;
  verificationStatus?: 'scheduled' | 'checked-in' | 'payroll-locked';
  geofenceStatus?: GeofenceStatus;
  distanceFromClientMeters?: number;
  gpsAccuracyMeters?: number;
  scheduleStatus?: VisitScheduleStatus;
  verificationExceptions?: string[];
}

interface TimesheetData {
  period: string;
  hours: number;
  status: 'draft' | 'submitted' | 'approved' | 'paid';
}

interface CredentialData {
  title: string;
  expiresIn: string;
  severity: 'critical' | 'warning' | 'info';
}

interface DashboardStats {
  todayVisitsCount: number;
  todayHoursScheduled: number;
  weekHoursWorked: number;
  activeClientsCount: number;
}

interface TaskRecord {
  id: string;
  visitIndex: number;
  clientName: string;
  task: string;
  status: 'completed' | 'not-completed';
  note: string;
  recordedAt: string;
  synced: boolean;
}

interface ConcernRecord {
  id: string;
  clientName: string;
  category: 'wellbeing' | 'medication' | 'safeguarding' | 'environment' | 'other';
  severity: 'low' | 'medium' | 'high';
  details: string;
  actionTaken: string;
  recordedAt: string;
  synced: boolean;
}

interface CareNoteRecord {
  id: string;
  visitId: string;
  clientName: string;
  visitType: string;
  note: string;
  recordedAt: string;
  synced: boolean;
}

interface OfflineQueueItem {
  id: string;
  type: 'check-in' | 'check-out' | 'task-record' | 'concern' | 'timesheet' | 'care-note';
  label: string;
  createdAt: string;
  payload: unknown;
}

type GpsPermissionState = 'checking' | 'granted' | 'prompt' | 'denied' | 'unsupported';

interface GpsFix {
  latitude: number;
  longitude: number;
  accuracy?: number;
  checkedAt: string;
}

type CarerTab = 'visits' | 'tasks' | 'concerns' | 'timesheet' | 'offline';
type HoursPeriod = 'day' | 'week' | 'month';

const createId = (prefix: string) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const PAYROLL_TIMESHEETS_KEY = 'admin:carer-payroll-timesheets';
const CARE_NOTES_KEY = 'carer:care-notes';

interface PayrollTimesheetEntry {
  id: string;
  caregiverId: string;
  date: string;
  hours: number;
  notes: string;
  source: 'carer-app' | 'manual';
  clientName?: string;
  status?: 'draft' | 'submitted' | 'approved' | 'paid';
  recordedBy?: string;
  recordedAt: string;
}

const caregiverProfiles: Record<string, { id: string; name: string }> = {
  'adrianpopescu@carer.com': { id: 'carer-adrian', name: 'Adrian Popescu' },
  'daniel.moore@fuchsiahomecare.co.uk': { id: 'carer-daniel', name: 'Daniel Moore' },
  'amelia.patel@fuchsiahomecare.co.uk': { id: 'carer-amelia', name: 'Amelia Patel' },
  'oliver.reed@fuchsiahomecare.co.uk': { id: 'carer-oliver', name: 'Oliver Reed' },
  'grace.morgan@fuchsiahomecare.co.uk': { id: 'carer-grace', name: 'Grace Morgan' },
};

const CLIENT_COORDINATES: Record<string, { latitude: number; longitude: number }> = {
  'Margaret Williams': { latitude: 52.5868, longitude: 1.7235 },
  'George Taylor': { latitude: 52.6124, longitude: 1.7048 },
  'Eileen Cooper': { latitude: 52.6041, longitude: 1.7314 },
  'Arthur Bennett': { latitude: 52.6117, longitude: 1.7247 },
  'Rose Hughes': { latitude: 52.6028, longitude: 1.7378 },
};

const GEOFENCE_LIMIT_METERS = 200;
const LATE_CHECK_IN_LIMIT_MINUTES = 10;
const MINIMUM_VISIT_DURATION_MINUTES = 15;

function getCaregiverPayrollProfile(email?: string, name?: string) {
  if (!email) {
    return { id: 'carer-adrian', name: name || 'Adrian Popescu' };
  }

  return caregiverProfiles[email.toLowerCase()] ?? { id: 'carer-adrian', name: name || email };
}

function parseVisitHours(duration: string): number {
  const match = duration.match(/(\d+(?:\.\d+)?)/);
  return match ? Number(match[1]) : 0;
}

function parseVisitDurationMinutes(duration: string): number {
  const hours = parseVisitHours(duration);
  return Math.max(0, Math.round(hours * 60));
}

function parseVisitStartAt(visit: VisitData): Date {
  const normalizedTime = visit.time.trim().toUpperCase();
  const match = normalizedTime.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?$/);
  let hours = match ? Number(match[1]) : 0;
  const minutes = match?.[2] ? Number(match[2]) : 0;
  const meridiem = match?.[3];

  if (meridiem === 'PM' && hours < 12) hours += 12;
  if (meridiem === 'AM' && hours === 12) hours = 0;

  return new Date(`${visit.scheduledDate}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`);
}

function getVisitWindow(visit: VisitData) {
  const start = parseVisitStartAt(visit);
  const end = new Date(start.getTime() + parseVisitDurationMinutes(visit.duration) * 60_000);

  return {
    scheduledStartAt: start.toISOString(),
    scheduledEndAt: end.toISOString(),
    scheduledDurationMinutes: parseVisitDurationMinutes(visit.duration),
  };
}

function distanceMeters(from: { latitude: number; longitude: number }, to: { latitude: number; longitude: number }): number {
  const earthRadiusMeters = 6_371_000;
  const fromLatitude = (from.latitude * Math.PI) / 180;
  const toLatitude = (to.latitude * Math.PI) / 180;
  const latitudeDelta = ((to.latitude - from.latitude) * Math.PI) / 180;
  const longitudeDelta = ((to.longitude - from.longitude) * Math.PI) / 180;
  const haversine =
    Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(fromLatitude) * Math.cos(toLatitude) * Math.sin(longitudeDelta / 2) ** 2;

  return earthRadiusMeters * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
}

function createAuditEvent(
  type: VisitAuditEvent['type'],
  label: string,
  location?: {
    latitude: number;
    longitude: number;
    locationSource: 'device-gps' | 'scheduled-address';
    distanceFromClientMeters?: number;
  }
): VisitAuditEvent {
  return {
    id: createId(type),
    type,
    label,
    createdAt: new Date().toISOString(),
    locationSource: location?.locationSource,
    latitude: location?.latitude,
    longitude: location?.longitude,
    distanceFromClientMeters: location?.distanceFromClientMeters,
  };
}

function todayIso(): string {
  return toLocalIsoDate(new Date());
}

function toLocalIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getWeekStart(dateString: string): string {
  const date = new Date(`${dateString}T00:00:00`);
  const day = date.getDay() || 7;
  date.setDate(date.getDate() - day + 1);
  return toLocalIsoDate(date);
}

function formatDate(dateString: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(`${dateString}T00:00:00`));
}

function periodMatches(entryDate: string, period: HoursPeriod, selectedDate: string): boolean {
  if (period === 'day') {
    return entryDate === selectedDate;
  }

  if (period === 'week') {
    return getWeekStart(entryDate) === getWeekStart(selectedDate);
  }

  return entryDate.slice(0, 7) === selectedDate.slice(0, 7);
}

function periodLabel(period: HoursPeriod, selectedDate: string): string {
  if (period === 'day') {
    return formatDate(selectedDate);
  }

  if (period === 'week') {
    const weekStart = getWeekStart(selectedDate);
    const weekEnd = new Date(`${weekStart}T00:00:00`);
    weekEnd.setDate(weekEnd.getDate() + 6);
    return `${formatDate(weekStart)} - ${formatDate(toLocalIsoDate(weekEnd))}`;
  }

  return new Intl.DateTimeFormat('en-GB', {
    month: 'long',
    year: 'numeric',
  }).format(new Date(`${selectedDate.slice(0, 7)}-01T00:00:00`));
}

function totalPayrollHours(entries: PayrollTimesheetEntry[]): number {
  return entries.reduce((sum, entry) => sum + entry.hours, 0);
}

function formatHours(hours: number): string {
  return new Intl.NumberFormat('en-GB', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  }).format(hours);
}

function defaultCareNote(visit: VisitData, taskRecords: TaskRecord[], concerns: ConcernRecord[]): string {
  const visitTasks = taskRecords.filter((record) => record.clientName === visit.clientName);
  const completedTasks = visitTasks.filter((record) => record.status === 'completed').map((record) => record.task);
  const notCompletedTasks = visitTasks.filter((record) => record.status === 'not-completed').map((record) => record.task);
  const visitConcerns = concerns.filter((concern) => concern.clientName === visit.clientName);

  return [
    `Care note for ${visit.clientName} - ${visit.visitType}.`,
    `Visit time: ${visit.time}, planned duration: ${visit.duration}.`,
    completedTasks.length > 0
      ? `Completed tasks: ${completedTasks.join(', ')}.`
      : 'Completed tasks: no task evidence recorded yet.',
    notCompletedTasks.length > 0 ? `Tasks not completed: ${notCompletedTasks.join(', ')}.` : '',
    visitConcerns.length > 0
      ? `Concerns recorded: ${visitConcerns.map((concern) => `${concern.category} - ${concern.details}`).join('; ')}.`
      : 'No concerns recorded during this visit.',
    'Handover: continue with the current care plan and review any exceptions with the coordinator.',
  ].filter(Boolean).join('\n');
}

function getVisitTaskRecord(taskRecords: TaskRecord[], visitIndex: number, task: string): TaskRecord | undefined {
  return taskRecords.find((record) => record.visitIndex === visitIndex && record.task === task);
}

function getPendingVisitTasks(visit: VisitData, visitIndex: number, taskRecords: TaskRecord[]): string[] {
  return visit.tasks.filter((task) => !getVisitTaskRecord(taskRecords, visitIndex, task));
}

async function getCaregiverLocationFallback(
  visit: VisitData,
  options: {
    requireDeviceGps?: boolean;
    onGpsFix?: (fix: GpsFix) => void;
  } = {}
): Promise<{
  latitude: number;
  longitude: number;
  locationSource: 'device-gps' | 'scheduled-address';
  address: string;
  accuracy?: number;
}> {
  const clientCoordinates = CLIENT_COORDINATES[visit.clientName] ?? CLIENT_COORDINATES['Margaret Williams']!;
  const fallbackLocation = {
    latitude: clientCoordinates.latitude,
    longitude: clientCoordinates.longitude,
    locationSource: 'scheduled-address' as const,
    address: visit.address,
  };

  if (!navigator.geolocation) {
    if (options.requireDeviceGps) {
      throw new Error('Browser GPS is not supported on this device.');
    }

    return fallbackLocation;
  }

  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      if (options.requireDeviceGps) {
        reject(new Error('Browser GPS timed out before a location was provided.'));
        return;
      }

      resolve(fallbackLocation);
    }, options.requireDeviceGps ? 10000 : 3500);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        window.clearTimeout(timeout);
        options.onGpsFix?.({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          checkedAt: new Date().toISOString(),
        });
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          locationSource: 'device-gps',
          address: visit.address,
          accuracy: position.coords.accuracy,
        });
      },
      (error) => {
        window.clearTimeout(timeout);
        if (options.requireDeviceGps) {
          reject(new Error(error.message || 'Browser did not provide GPS permission.'));
          return;
        }

        resolve(fallbackLocation);
      },
      {
        enableHighAccuracy: true,
        maximumAge: 60_000,
        timeout: options.requireDeviceGps ? 9000 : 3000,
      }
    );
  });
}

function gpsPermissionText(status: GpsPermissionState): string {
  switch (status) {
    case 'granted':
      return 'GPS allowed';
    case 'prompt':
      return 'GPS permission needed';
    case 'denied':
      return 'GPS blocked';
    case 'unsupported':
      return 'GPS unsupported';
    default:
      return 'Checking GPS';
  }
}

function gpsPermissionClasses(status: GpsPermissionState): string {
  switch (status) {
    case 'granted':
      return 'bg-emerald-100 text-emerald-800';
    case 'denied':
    case 'unsupported':
      return 'bg-red-100 text-red-800';
    case 'prompt':
      return 'bg-amber-100 text-amber-800';
    default:
      return 'bg-slate-100 text-slate-700';
  }
}

export const CaregiverDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const connectionStatus = useConnectionStatus();

  // State for data with localStorage fallback
  const [visits, setVisits] = useState<VisitData[]>(() =>
    getLocalStorage(CACHE_KEYS.CAREGIVER_DASHBOARD_VISITS, [])
  );
  const [timesheets, setTimesheets] = useState<TimesheetData[]>(() =>
    getLocalStorage(CACHE_KEYS.CAREGIVER_DASHBOARD_TIMESHEETS, [])
  );
  const [credentials, setCredentials] = useState<CredentialData[]>(() =>
    getLocalStorage(CACHE_KEYS.CAREGIVER_DASHBOARD_CREDENTIALS, [])
  );
  const [stats, setStats] = useState<DashboardStats>(() =>
    getLocalStorage(CACHE_KEYS.CAREGIVER_DASHBOARD_STATS, {
      todayVisitsCount: 0,
      todayHoursScheduled: 0,
      weekHoursWorked: 0,
      activeClientsCount: 0,
    })
  );
  const [activeTab, setActiveTab] = useState<CarerTab>('visits');
  const [selectedVisit, setSelectedVisit] = useState<VisitData | null>(null);
  const [taskRecords, setTaskRecords] = useState<TaskRecord[]>(() =>
    getLocalStorage(CACHE_KEYS.CAREGIVER_TASK_RECORDS, [])
  );
  const [concerns, setConcerns] = useState<ConcernRecord[]>(() =>
    getLocalStorage(CACHE_KEYS.CAREGIVER_CONCERNS, [])
  );
  const [careNotes, setCareNotes] = useState<CareNoteRecord[]>(() =>
    getLocalStorage(CARE_NOTES_KEY, [])
  );
  const [offlineQueue, setOfflineQueue] = useState<OfflineQueueItem[]>(() =>
    getLocalStorage(CACHE_KEYS.CAREGIVER_OFFLINE_QUEUE, [])
  );
  const [payrollEntries, setPayrollEntries] = useState<PayrollTimesheetEntry[]>(() =>
    getLocalStorage(PAYROLL_TIMESHEETS_KEY, [])
  );
  const [hoursPeriod, setHoursPeriod] = useState<HoursPeriod>('week');
  const [hoursDate, setHoursDate] = useState(todayIso());
  const [gpsPermission, setGpsPermission] = useState<GpsPermissionState>('checking');
  const [lastGpsFix, setLastGpsFix] = useState<GpsFix | null>(null);
  const [taskNotes, setTaskNotes] = useState<Record<string, string>>({});
  const [careNoteDrafts, setCareNoteDrafts] = useState<Record<string, string>>({});
  const [isGeneratingCareNote, setIsGeneratingCareNote] = useState(false);
  const [isDictating, setIsDictating] = useState(false);
  const [concernForm, setConcernForm] = useState({
    clientName: 'Margaret Williams',
    category: 'wellbeing' as ConcernRecord['category'],
    severity: 'medium' as ConcernRecord['severity'],
    details: '',
    actionTaken: '',
  });
  const caregiverPayrollProfile = useMemo(
    () => getCaregiverPayrollProfile(user?.email, user?.name),
    [user?.email, user?.name]
  );

  const refreshPayrollEntries = useCallback(() => {
    setPayrollEntries(getLocalStorage(PAYROLL_TIMESHEETS_KEY, []));
  }, []);

  const caregiverPayrollEntries = useMemo(
    () =>
      payrollEntries
        .filter((entry) => entry.caregiverId === caregiverPayrollProfile.id)
        .sort((a, b) => b.date.localeCompare(a.date)),
    [payrollEntries, caregiverPayrollProfile.id]
  );

  const selectedPeriodEntries = useMemo(
    () => caregiverPayrollEntries.filter((entry) => periodMatches(entry.date, hoursPeriod, hoursDate)),
    [caregiverPayrollEntries, hoursPeriod, hoursDate]
  );

  const todayHours = useMemo(
    () => totalPayrollHours(caregiverPayrollEntries.filter((entry) => periodMatches(entry.date, 'day', todayIso()))),
    [caregiverPayrollEntries]
  );
  const currentWeekHours = useMemo(
    () => totalPayrollHours(caregiverPayrollEntries.filter((entry) => periodMatches(entry.date, 'week', todayIso()))),
    [caregiverPayrollEntries]
  );
  const currentMonthHours = useMemo(
    () => totalPayrollHours(caregiverPayrollEntries.filter((entry) => periodMatches(entry.date, 'month', todayIso()))),
    [caregiverPayrollEntries]
  );
  const selectedPeriodHours = totalPayrollHours(selectedPeriodEntries);
  const activeVisitIndex = useMemo(() => {
    const inProgressIndex = visits.findIndex((visit) => visit.status === 'in-progress');
    if (inProgressIndex >= 0) return inProgressIndex;

    const upcomingTodayIndex = visits.findIndex(
      (visit) => visit.status === 'upcoming' && visit.scheduledDate === todayIso()
    );
    return upcomingTodayIndex >= 0 ? upcomingTodayIndex : 0;
  }, [visits]);
  const activeVisit = visits[activeVisitIndex] ?? null;
  const activePendingTasks = activeVisit
    ? getPendingVisitTasks(activeVisit, activeVisitIndex, taskRecords)
    : [];
  const activeCompletedTaskCount = activeVisit ? activeVisit.tasks.length - activePendingTasks.length : 0;
  const activeCareNote = activeVisit ? careNoteDrafts[activeVisit.id] ?? '' : '';
  const activeHandoverSummary = activeVisit
    ? defaultCareNote(activeVisit, taskRecords, concerns)
    : 'No active visit selected yet.';

  // Track refresh state for each widget
  const [isRefreshingVisits, setIsRefreshingVisits] = useState(false);
  const [isRefreshingTimesheets, setIsRefreshingTimesheets] = useState(false);
  const [isRefreshingCredentials, setIsRefreshingCredentials] = useState(false);
  const [isRefreshingStats, setIsRefreshingStats] = useState(false);

  // Track last updated times
  const [visitsLastUpdated, setVisitsLastUpdated] = useState<Date | null>(
    () => getLastUpdated(CACHE_KEYS.CAREGIVER_DASHBOARD_VISITS)
  );
  const [timesheetsLastUpdated, setTimesheetsLastUpdated] = useState<Date | null>(
    () => getLastUpdated(CACHE_KEYS.CAREGIVER_DASHBOARD_TIMESHEETS)
  );
  const [credentialsLastUpdated, setCredentialsLastUpdated] = useState<Date | null>(
    () => getLastUpdated(CACHE_KEYS.CAREGIVER_DASHBOARD_CREDENTIALS)
  );
  const [statsLastUpdated, setStatsLastUpdated] = useState<Date | null>(
    () => getLastUpdated(CACHE_KEYS.CAREGIVER_DASHBOARD_STATS)
  );

  // Fetch visits data
  const fetchVisits = useCallback(async () => {
    setIsRefreshingVisits(true);
    try {
      // API integration pending - using local operational data.
      // Future: const response = await fetch('/api/caregiver/visits/today');
      // Future: const data = await response.json();

      const portalVisits: VisitData[] = [
        {
          id: 'visit-margaret-morning',
          clientName: 'Margaret Williams',
          visitType: 'Personal Care',
          scheduledDate: todayIso(),
          time: '9:00 AM',
          duration: '1 hour',
          address: 'Yarmouth Business Park, Suffolk Rd, Great Yarmouth, NR31 0LN',
          clientPhone: '01493738488',
          status: 'upcoming',
          tasks: [
            'Morning personal care',
            'Medication reminder',
            'Breakfast and hydration prompt',
          ],
          verificationStatus: 'scheduled',
        },
        {
          id: 'visit-george-companionship',
          clientName: 'George Taylor',
          visitType: 'Companionship',
          scheduledDate: todayIso(),
          time: '10:30 AM',
          duration: '1.5 hours',
          address: 'Suffolk Road, Great Yarmouth, NR31 0LN',
          clientPhone: '01493738499',
          status: 'upcoming',
          tasks: [
            'Conversation and wellbeing check',
            'Short assisted walk',
            'Document observations',
          ],
          verificationStatus: 'scheduled',
        },
        {
          id: 'visit-eileen-dementia',
          clientName: 'Eileen Cooper',
          visitType: 'Dementia Support',
          scheduledDate: todayIso(),
          time: '2:00 PM',
          duration: '1 hour',
          address: 'River View, Great Yarmouth, NR31 0LN',
          clientPhone: '01493738501',
          status: 'upcoming',
          tasks: [
            'Hydration prompts',
            'Orientation support',
            'Safety check',
          ],
          verificationStatus: 'scheduled',
        },
      ];

      setVisits(portalVisits);
      setLocalStorage(CACHE_KEYS.CAREGIVER_DASHBOARD_VISITS, portalVisits, CACHE_EXPIRATION.FIFTEEN_MINUTES);
      setVisitsLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching visits:', error);
      // Keep using cached data
    } finally {
      setIsRefreshingVisits(false);
    }
  }, []);

  // Fetch timesheets data
  const fetchTimesheets = useCallback(async () => {
    setIsRefreshingTimesheets(true);
    try {
      // API integration pending - using local operational data.
      const portalTimesheets: TimesheetData[] = [
        {
          period: 'Current Week',
          hours: 18.5,
          status: 'draft',
        },
        {
          period: 'Last Week',
          hours: 34,
          status: 'submitted',
        },
        {
          period: 'Previous Week',
          hours: 32,
          status: 'approved',
        },
        {
          period: 'Paid Week',
          hours: 36,
          status: 'paid',
        },
      ];

      setTimesheets(portalTimesheets);
      setLocalStorage(CACHE_KEYS.CAREGIVER_DASHBOARD_TIMESHEETS, portalTimesheets, CACHE_EXPIRATION.FIFTEEN_MINUTES);
      setTimesheetsLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching timesheets:', error);
    } finally {
      setIsRefreshingTimesheets(false);
    }
  }, []);

  // Fetch credentials data
  const fetchCredentials = useCallback(async () => {
    setIsRefreshingCredentials(true);
    try {
      // API integration pending - using local operational data.
      const portalCredentials: CredentialData[] = [
        {
          title: 'Care Certificate',
          expiresIn: '120 days',
          severity: 'info',
        },
        {
          title: 'Moving & Handling Training',
          expiresIn: '45 days',
          severity: 'warning',
        },
      ];

      setCredentials(portalCredentials);
      setLocalStorage(CACHE_KEYS.CAREGIVER_DASHBOARD_CREDENTIALS, portalCredentials, CACHE_EXPIRATION.ONE_HOUR);
      setCredentialsLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching credentials:', error);
    } finally {
      setIsRefreshingCredentials(false);
    }
  }, []);

  // Fetch stats data
  const fetchStats = useCallback(async () => {
    setIsRefreshingStats(true);
    try {
      // API integration pending - using local operational data.
      const portalStats: DashboardStats = {
        todayVisitsCount: 3,
        todayHoursScheduled: 3.5,
        weekHoursWorked: 18.5,
        activeClientsCount: 3,
      };

      setStats(portalStats);
      setLocalStorage(CACHE_KEYS.CAREGIVER_DASHBOARD_STATS, portalStats, CACHE_EXPIRATION.FIFTEEN_MINUTES);
      setStatsLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setIsRefreshingStats(false);
    }
  }, []);

  // Refresh all data
  const refreshAll = useCallback(async () => {
    await Promise.all([
      fetchVisits(),
      fetchTimesheets(),
      fetchCredentials(),
      fetchStats(),
    ]);
  }, [fetchVisits, fetchTimesheets, fetchCredentials, fetchStats]);

  const persistOfflineQueue = useCallback((items: OfflineQueueItem[]) => {
    setOfflineQueue(items);
    setLocalStorage(CACHE_KEYS.CAREGIVER_OFFLINE_QUEUE, items, CACHE_EXPIRATION.ONE_DAY);
  }, []);

  const queueOfflineAction = useCallback((item: Omit<OfflineQueueItem, 'id' | 'createdAt'>) => {
    const nextItem: OfflineQueueItem = {
      id: createId(item.type),
      createdAt: new Date().toISOString(),
      ...item,
    };
    persistOfflineQueue([nextItem, ...offlineQueue].slice(0, 50));
  }, [offlineQueue, persistOfflineQueue]);

  const pushPayrollEntry = useCallback((entry: Omit<PayrollTimesheetEntry, 'id' | 'recordedAt'>) => {
    const currentEntries = getLocalStorage<PayrollTimesheetEntry[]>(PAYROLL_TIMESHEETS_KEY, []);
    const duplicateExists = currentEntries.some(
      (current) =>
        current.source === 'carer-app' &&
        current.caregiverId === entry.caregiverId &&
        current.date === entry.date &&
        current.clientName === entry.clientName &&
        current.notes === entry.notes
    );

    if (duplicateExists) {
      return;
    }

    const nextEntries: PayrollTimesheetEntry[] = [
      {
        id: createId('carer-payroll'),
        recordedAt: new Date().toISOString(),
        ...entry,
      },
      ...currentEntries,
    ].slice(0, 250);

    setLocalStorage(PAYROLL_TIMESHEETS_KEY, nextEntries);
    setPayrollEntries(nextEntries);
  }, []);

  const pushLiveCarerStatus = useCallback(async (
    visit: VisitData,
    status: LiveCarerTrackingRecord['status'],
    currentTask: string,
    payrollLocked = false,
    verification?: VisitVerificationRecord
  ) => {
    const profile = getCaregiverPayrollProfile(user?.email, user?.name);
    const lastLocationAudit = verification?.auditTrail
      .filter((event) => typeof event.latitude === 'number' && typeof event.longitude === 'number')
      .at(-1);
    const location = verification
      ? {
          latitude: lastLocationAudit?.latitude ?? CLIENT_COORDINATES[visit.clientName]?.latitude ?? 52.5868,
          longitude: lastLocationAudit?.longitude ?? CLIENT_COORDINATES[visit.clientName]?.longitude ?? 1.7235,
          locationSource: lastLocationAudit?.locationSource ?? ('scheduled-address' as const),
          address: visit.address,
          accuracy: verification.gpsAccuracyMeters,
        }
      : await getCaregiverLocationFallback(visit);
    const nextRecord: LiveCarerTrackingRecord = {
      caregiverId: profile.id,
      caregiverName: profile.name,
      email: user?.email ?? 'AdrianPopescu@carer.com',
      status,
      currentClient: visit.clientName,
      currentVisit: visit.visitType,
      currentTask,
      address: visit.address,
      latitude: location.latitude,
      longitude: location.longitude,
      locationSource: location.locationSource,
      scheduleMatched: visit.scheduledDate === todayIso(),
      payrollLocked,
      lastUpdated: new Date().toISOString(),
      activeVisitId: visit.id,
      scheduledStartAt: verification?.scheduledStartAt,
      scheduledEndAt: verification?.scheduledEndAt,
      actualCheckInAt: verification?.actualCheckInAt,
      actualCheckOutAt: verification?.actualCheckOutAt,
      scheduledDurationMinutes: verification?.scheduledDurationMinutes,
      realDurationMinutes: verification?.realDurationMinutes,
      varianceMinutes: verification?.varianceMinutes,
      distanceFromClientMeters: verification?.distanceFromClientMeters,
      gpsAccuracyMeters: verification?.gpsAccuracyMeters,
      geofenceStatus: verification?.geofenceStatus,
      scheduleStatus: verification?.scheduleStatus,
      exceptions: verification?.exceptions,
      taskEvidence: verification?.taskEvidence,
      auditTrail: verification?.auditTrail,
    };

    await publishLiveCarerStatus(nextRecord);
  }, [user?.email, user?.name]);

  // Navigation handlers to avoid void operator issues
  const handleNavigateToCredentials = useCallback(() => {
    navigate('/caregivers/caregiver-1/training');
  }, [navigate]);

  const handleNavigateToSchedule = useCallback(() => {
    navigate('/scheduling');
  }, [navigate]);

  const handleNavigateToTimeTracking = useCallback(() => {
    navigate('/time-tracking');
  }, [navigate]);

  const handleNavigateToTraining = useCallback(() => {
    navigate('/caregivers/caregiver-1/training');
  }, [navigate]);

  const openDirections = useCallback((visit: VisitData) => {
    const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(visit.address)}`;
    const mapsWindow = window.open(url, '_blank', 'noopener,noreferrer');

    if (!mapsWindow) {
      window.location.href = url;
    }
  }, []);

  const callClient = useCallback((visit: VisitData) => {
    if (!visit.clientPhone) {
      toast.error('No client phone number available');
      return;
    }

    window.location.href = `tel:${visit.clientPhone}`;
  }, []);

  const viewVisitDetails = useCallback((visit: VisitData) => {
    setSelectedVisit(visit);
  }, []);

  const refreshGpsPermission = useCallback(async () => {
    if (!navigator.geolocation) {
      setGpsPermission('unsupported');
      return 'unsupported' as GpsPermissionState;
    }

    if (!navigator.permissions?.query) {
      setGpsPermission('prompt');
      return 'prompt' as GpsPermissionState;
    }

    try {
      const status = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
      const nextStatus = status.state as GpsPermissionState;
      setGpsPermission(nextStatus);
      return nextStatus;
    } catch {
      setGpsPermission('prompt');
      return 'prompt' as GpsPermissionState;
    }
  }, []);

  const requestGpsAccess = useCallback(() => {
    if (!navigator.geolocation) {
      setGpsPermission('unsupported');
      toast.error('This browser or device does not support GPS location.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setGpsPermission('granted');
        setLastGpsFix({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          checkedAt: new Date().toISOString(),
        });
        toast.success('GPS permission is active for carer visit control');
      },
      (error) => {
        setGpsPermission(error.code === error.PERMISSION_DENIED ? 'denied' : 'prompt');
        toast.error(
          error.code === error.PERMISSION_DENIED
            ? 'GPS is blocked. Allow Location in the browser settings to use check-in control.'
            : 'The browser could not read GPS. Try again from the client address.'
        );
      },
      {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 10000,
      }
    );
  }, []);

  // Initial data load
  useEffect(() => {
    void refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    let isMounted = true;

    if (!navigator.geolocation) {
      setGpsPermission('unsupported');
      return () => {
        isMounted = false;
      };
    }

    if (!navigator.permissions?.query) {
      setGpsPermission('prompt');
      return () => {
        isMounted = false;
      };
    }

    navigator.permissions
      .query({ name: 'geolocation' as PermissionName })
      .then((status) => {
        if (!isMounted) return;
        setGpsPermission(status.state as GpsPermissionState);
        status.onchange = () => {
          if (isMounted) {
            setGpsPermission(status.state as GpsPermissionState);
          }
        };
      })
      .catch(() => {
        if (isMounted) {
          setGpsPermission('prompt');
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const handleStorageUpdate = (event: StorageEvent) => {
      if (event.key === PAYROLL_TIMESHEETS_KEY) {
        refreshPayrollEntries();
      }
    };

    window.addEventListener('storage', handleStorageUpdate);
    window.addEventListener('focus', refreshPayrollEntries);

    return () => {
      window.removeEventListener('storage', handleStorageUpdate);
      window.removeEventListener('focus', refreshPayrollEntries);
    };
  }, [refreshPayrollEntries]);

  // Optimistic UI for check-in/check-out
  const handleCheckIn = async (visitIndex: number) => {
    const visit = visits[visitIndex];
    if (!visit) return;

    if (visit.scheduledDate !== todayIso()) {
      toast.error('Check-in blocked: this visit is not scheduled for today.');
      return;
    }

    if (visit.status !== 'upcoming') {
      toast.error('Check-in blocked: this visit is not in upcoming status.');
      return;
    }

    const profile = getCaregiverPayrollProfile(user?.email, user?.name);
    const visitWindow = getVisitWindow(visit);
    const clientCoordinates = CLIENT_COORDINATES[visit.clientName] ?? CLIENT_COORDINATES['Margaret Williams']!;
    const currentGpsPermission = await refreshGpsPermission();

    if (currentGpsPermission === 'denied' || currentGpsPermission === 'unsupported') {
      const blockedRecord: VisitVerificationRecord = {
        visitId: visit.id,
        caregiverId: profile.id,
        caregiverName: profile.name,
        clientName: visit.clientName,
        ...visitWindow,
        geofenceStatus: 'fallback',
        scheduleStatus: 'unscheduled',
        payrollLocked: false,
        hoursBlocked: true,
        exceptions: ['Browser GPS permission is missing; check-in blocked and hours were not added'],
        taskEvidence: [],
        auditTrail: [
          createAuditEvent('exception', 'Check-in blocked because the browser did not provide GPS permission', {
            latitude: clientCoordinates.latitude,
            longitude: clientCoordinates.longitude,
            locationSource: 'scheduled-address',
          }),
        ],
        updatedAt: new Date().toISOString(),
      };

      upsertStoredVisitVerificationRecord(blockedRecord);
      await pushLiveCarerStatus(visit, 'available', 'Check-in blocked: browser GPS permission missing', false, blockedRecord);
      toast.error('Check-in blocked: allow GPS location in the browser first.');
      return;
    }

    let location: Awaited<ReturnType<typeof getCaregiverLocationFallback>>;

    try {
      location = await getCaregiverLocationFallback(visit, {
        requireDeviceGps: true,
        onGpsFix: setLastGpsFix,
      });
      setGpsPermission('granted');
    } catch (error) {
      const blockedRecord: VisitVerificationRecord = {
        visitId: visit.id,
        caregiverId: profile.id,
        caregiverName: profile.name,
        clientName: visit.clientName,
        ...visitWindow,
        geofenceStatus: 'fallback',
        scheduleStatus: 'unscheduled',
        payrollLocked: false,
        hoursBlocked: true,
        exceptions: ['Browser GPS did not return a live location; check-in blocked and hours were not added'],
        taskEvidence: [],
        auditTrail: [
          createAuditEvent('exception', error instanceof Error ? error.message : 'Browser GPS did not return a live location', {
            latitude: clientCoordinates.latitude,
            longitude: clientCoordinates.longitude,
            locationSource: 'scheduled-address',
          }),
        ],
        updatedAt: new Date().toISOString(),
      };

      upsertStoredVisitVerificationRecord(blockedRecord);
      await pushLiveCarerStatus(visit, 'available', 'Check-in blocked: live GPS unavailable', false, blockedRecord);
      void refreshGpsPermission();
      toast.error('Check-in blocked: the browser must provide live GPS for visit control.');
      return;
    }

    const distanceFromClientMeters = distanceMeters(location, clientCoordinates);
    const geofenceStatus: GeofenceStatus =
      location.locationSource === 'scheduled-address'
        ? 'fallback'
        : distanceFromClientMeters <= GEOFENCE_LIMIT_METERS
          ? 'inside'
          : 'outside';
    const minutesFromStart = Math.round(
      (Date.now() - new Date(visitWindow.scheduledStartAt).getTime()) / 60_000
    );
    const scheduleStatus: VisitScheduleStatus =
      minutesFromStart > LATE_CHECK_IN_LIMIT_MINUTES ? 'late' : minutesFromStart < -30 ? 'early' : 'on-time';
    const exceptions = [
      geofenceStatus === 'outside' && `GPS outside client address by ${Math.round(distanceFromClientMeters)}m`,
      geofenceStatus === 'fallback' && 'Device GPS unavailable; scheduled address fallback used for preview',
      scheduleStatus === 'late' && `Check-in ${minutesFromStart} minutes after scheduled start`,
      scheduleStatus === 'early' && `Check-in ${Math.abs(minutesFromStart)} minutes before scheduled start`,
      typeof location.accuracy === 'number' && location.accuracy > 100 && `Low GPS accuracy: ${Math.round(location.accuracy)}m`,
    ].filter(Boolean) as string[];

    if (geofenceStatus === 'outside') {
      const blockedRecord: VisitVerificationRecord = {
        visitId: visit.id,
        caregiverId: profile.id,
        caregiverName: profile.name,
        clientName: visit.clientName,
        ...visitWindow,
        distanceFromClientMeters,
        gpsAccuracyMeters: location.accuracy,
        geofenceStatus,
        scheduleStatus: 'unscheduled',
        payrollLocked: false,
        hoursBlocked: true,
        exceptions,
        taskEvidence: [],
        auditTrail: [
          createAuditEvent('gps-check', 'Check-in blocked because GPS is outside the client geofence', {
            latitude: location.latitude,
            longitude: location.longitude,
            locationSource: location.locationSource,
            distanceFromClientMeters,
          }),
          createAuditEvent('exception', exceptions.join('; ')),
        ],
        updatedAt: new Date().toISOString(),
      };
      upsertStoredVisitVerificationRecord(blockedRecord);
      await pushLiveCarerStatus(visit, 'available', 'Check-in blocked: GPS outside client address', false, blockedRecord);
      toast.error('Check-in blocked: GPS does not match the client address. Hours were not added.');
      return;
    }

    const verificationRecord: VisitVerificationRecord = {
      visitId: visit.id,
      caregiverId: profile.id,
      caregiverName: profile.name,
      clientName: visit.clientName,
      ...visitWindow,
      actualCheckInAt: new Date().toISOString(),
      distanceFromClientMeters,
      gpsAccuracyMeters: location.accuracy,
      geofenceStatus,
      scheduleStatus,
      payrollLocked: false,
      hoursBlocked: false,
      exceptions,
      taskEvidence: [],
      auditTrail: [
        createAuditEvent('gps-check', 'GPS checked against client address', {
          latitude: location.latitude,
          longitude: location.longitude,
          locationSource: location.locationSource,
          distanceFromClientMeters,
        }),
        createAuditEvent('check-in', 'Carer checked in from scheduled visit'),
        ...exceptions.map((exception) => createAuditEvent('exception', exception)),
      ],
      updatedAt: new Date().toISOString(),
    };

    // Optimistically update UI
    const updatedVisits = [...visits];
    if (updatedVisits[visitIndex]) {
      updatedVisits[visitIndex] = {
        ...updatedVisits[visitIndex]!,
        status: 'in-progress',
        checkInAt: verificationRecord.actualCheckInAt,
        verificationStatus: 'checked-in',
        geofenceStatus,
        distanceFromClientMeters,
        gpsAccuracyMeters: location.accuracy,
        scheduleStatus,
        verificationExceptions: exceptions,
      };
      setVisits(updatedVisits);
    }

    try {
      // API integration pending - mock implementation
      // Future: await fetch('/api/caregiver/visits/check-in', {
      //   method: 'POST',
      //   body: JSON.stringify({ visitId: visit.id }),
      // });
      
      // Update cache
      setLocalStorage(CACHE_KEYS.CAREGIVER_DASHBOARD_VISITS, updatedVisits, CACHE_EXPIRATION.FIFTEEN_MINUTES);
      setVisitsLastUpdated(new Date());
      queueOfflineAction({
        type: 'check-in',
        label: `Check-in recorded for ${visit.clientName}`,
        payload: {
          visitIndex,
          visitId: visit.id,
          scheduleMatched: true,
          scheduledDate: visit.scheduledDate,
          geofenceStatus,
          distanceFromClientMeters,
          gpsAccuracyMeters: location.accuracy,
          scheduleStatus,
          exceptions,
          recordedAt: verificationRecord.actualCheckInAt,
        },
      });
      upsertStoredVisitVerificationRecord(verificationRecord);
      await pushLiveCarerStatus(
        visit,
        'in-progress',
        visit.tasks[0] ?? `Started ${visit.visitType}`,
        false,
        verificationRecord
      );
      toast.success(geofenceStatus === 'fallback' ? 'Check-in saved with GPS fallback note' : 'Check-in verified by GPS and schedule');
    } catch (error) {
      console.error('Error checking in:', error);
      // Revert on error
      await fetchVisits();
    }
  };

  const handleCheckOut = async (visitIndex: number) => {
    const visit = visits[visitIndex];
    if (!visit) return;

    if (visit.scheduledDate !== todayIso()) {
      toast.error('Check-out blocked: this visit is not scheduled for today.');
      return;
    }

    if (visit.status !== 'in-progress') {
      toast.error('Check-out blocked: check in first from the scheduled visit card.');
      return;
    }

    const pendingTasks = getPendingVisitTasks(visit, visitIndex, taskRecords);
    if (pendingTasks.length > 0) {
      setActiveTab('tasks');
      toast.error(`Complete or mark not completed before check-out: ${pendingTasks.slice(0, 2).join(', ')}${pendingTasks.length > 2 ? '...' : ''}`);
      return;
    }

    const profile = getCaregiverPayrollProfile(user?.email, user?.name);
    const currentVerification = getStoredVisitVerificationRecords().find((record) => record.visitId === visit.id);
    const visitWindow = getVisitWindow(visit);
    const checkInAt = visit.checkInAt ?? currentVerification?.actualCheckInAt;
    const actualCheckOutAt = new Date().toISOString();
    const realDurationMinutes = checkInAt
      ? Math.max(0, Math.round((new Date(actualCheckOutAt).getTime() - new Date(checkInAt).getTime()) / 60_000))
      : 0;
    const scheduledDurationMinutes = currentVerification?.scheduledDurationMinutes ?? visitWindow.scheduledDurationMinutes;
    const varianceMinutes = realDurationMinutes - scheduledDurationMinutes;
    const checkoutExceptions = [
      ...new Set([
        ...(currentVerification?.exceptions ?? []),
        realDurationMinutes < MINIMUM_VISIT_DURATION_MINUTES && `Visit too short: ${realDurationMinutes} minutes recorded`,
        realDurationMinutes > scheduledDurationMinutes + 30 && `Visit overran scheduled time by ${realDurationMinutes - scheduledDurationMinutes} minutes`,
        realDurationMinutes < scheduledDurationMinutes - 15 && `Visit shorter than scheduled by ${scheduledDurationMinutes - realDurationMinutes} minutes`,
      ].filter(Boolean) as string[]),
    ];

    const completedVerification: VisitVerificationRecord = {
      visitId: visit.id,
      caregiverId: profile.id,
      caregiverName: profile.name,
      clientName: visit.clientName,
      ...visitWindow,
      actualCheckInAt: checkInAt,
      actualCheckOutAt,
      realDurationMinutes,
      varianceMinutes,
      distanceFromClientMeters: currentVerification?.distanceFromClientMeters ?? visit.distanceFromClientMeters,
      gpsAccuracyMeters: currentVerification?.gpsAccuracyMeters ?? visit.gpsAccuracyMeters,
      geofenceStatus: currentVerification?.geofenceStatus ?? visit.geofenceStatus ?? 'fallback',
      scheduleStatus: 'completed',
      payrollLocked: true,
      hoursBlocked: false,
      exceptions: checkoutExceptions,
      taskEvidence: currentVerification?.taskEvidence ?? [],
      auditTrail: [
        ...(currentVerification?.auditTrail ?? []),
        createAuditEvent('check-out', 'Carer checked out from scheduled visit'),
        createAuditEvent('payroll-lock', 'Payroll hours locked from scheduled visit and real check-in/out times'),
        ...checkoutExceptions
          .filter((exception) => !(currentVerification?.exceptions ?? []).includes(exception))
          .map((exception) => createAuditEvent('exception', exception)),
      ],
      updatedAt: new Date().toISOString(),
    };

    // Optimistically update UI
    const updatedVisits = [...visits];
    if (updatedVisits[visitIndex]) {
      updatedVisits[visitIndex] = {
        ...updatedVisits[visitIndex]!,
        status: 'completed',
        checkOutAt: actualCheckOutAt,
        verificationStatus: 'payroll-locked',
        geofenceStatus: completedVerification.geofenceStatus,
        distanceFromClientMeters: completedVerification.distanceFromClientMeters,
        gpsAccuracyMeters: completedVerification.gpsAccuracyMeters,
        scheduleStatus: completedVerification.scheduleStatus,
        verificationExceptions: checkoutExceptions,
      };
      setVisits(updatedVisits);
    }

    try {
      // API integration pending - mock implementation
      // Future: await fetch('/api/caregiver/visits/check-out', {
      //   method: 'POST',
      //   body: JSON.stringify({ visitId: visit.id }),
      // });
      
      // Update cache
      setLocalStorage(CACHE_KEYS.CAREGIVER_DASHBOARD_VISITS, updatedVisits, CACHE_EXPIRATION.FIFTEEN_MINUTES);
      setVisitsLastUpdated(new Date());
      pushPayrollEntry({
        caregiverId: profile.id,
        date: todayIso(),
        hours: scheduledDurationMinutes / 60,
        notes: `Verified visit: ${visit.visitType}. Scheduled ${scheduledDurationMinutes} min, real ${realDurationMinutes} min, variance ${varianceMinutes} min.`,
        source: 'carer-app',
        clientName: visit.clientName,
        status: 'draft',
        recordedBy: profile.name,
      });
      queueOfflineAction({
        type: 'check-out',
        label: `Verified check-out recorded for ${visit.clientName}`,
        payload: {
          visitIndex,
          visitId: visit.id,
          scheduleMatched: true,
          scheduledDate: visit.scheduledDate,
          scheduledDuration: visit.duration,
          scheduledDurationMinutes,
          realDurationMinutes,
          varianceMinutes,
          exceptions: checkoutExceptions,
          payrollHours: scheduledDurationMinutes / 60,
          recordedAt: actualCheckOutAt,
        },
      });
      upsertStoredVisitVerificationRecord(completedVerification);
      await pushLiveCarerStatus(visit, 'completed', 'Visit completed and payroll locked', true, completedVerification);
      toast.success('Payroll hours locked from scheduled visit duration');
    } catch (error) {
      console.error('Error checking out:', error);
      // Revert on error
      await fetchVisits();
    }
  };

  const handleTaskRecord = (visitIndex: number, task: string, status: TaskRecord['status']) => {
    const visit = visits[visitIndex];
    if (!visit) return;

    const noteKey = `${visitIndex}:${task}`;
    const record: TaskRecord = {
      id: createId('task'),
      visitIndex,
      clientName: visit.clientName,
      task,
      status,
      note: taskNotes[noteKey] ?? '',
      recordedAt: new Date().toISOString(),
      synced: connectionStatus.isConnected,
    };
    const nextRecords = [record, ...taskRecords].slice(0, 100);
    setTaskRecords(nextRecords);
    setLocalStorage(CACHE_KEYS.CAREGIVER_TASK_RECORDS, nextRecords, CACHE_EXPIRATION.ONE_DAY);
    const profile = getCaregiverPayrollProfile(user?.email, user?.name);
    const evidence: VisitTaskEvidence = {
      id: record.id,
      task,
      status,
      note: record.note,
      recordedAt: record.recordedAt,
      caregiverId: profile.id,
      caregiverName: profile.name,
    };
    const currentVerification = getStoredVisitVerificationRecords().find((verification) => verification.visitId === visit.id);
    if (currentVerification) {
      upsertStoredVisitVerificationRecord({
        ...currentVerification,
        taskEvidence: [evidence, ...currentVerification.taskEvidence.filter((item) => item.id !== evidence.id)].slice(0, 50),
        auditTrail: [
          ...currentVerification.auditTrail,
          createAuditEvent('task-evidence', `${status === 'completed' ? 'Completed' : 'Not completed'} task: ${task}`),
        ],
        updatedAt: new Date().toISOString(),
      });
    }
    queueOfflineAction({
      type: 'task-record',
      label: `${status === 'completed' ? 'Task completed' : 'Task not completed'}: ${task}`,
      payload: record,
    });
    setTaskNotes({ ...taskNotes, [noteKey]: '' });
    if (status === 'completed') {
      void pushLiveCarerStatus(visit, 'in-progress', task, false);
    }
  };

  const handleFillStandardTaskNotes = (visitIndex: number) => {
    const visit = visits[visitIndex];
    if (!visit) return;

    const nextNotes = { ...taskNotes };
    visit.tasks.forEach((task) => {
      const noteKey = `${visitIndex}:${task}`;
      if (!nextNotes[noteKey]) {
        nextNotes[noteKey] = `Supported ${visit.clientName} with ${task.toLowerCase()}. Outcome observed and recorded.`;
      }
    });
    setTaskNotes(nextNotes);
    setActiveTab('tasks');
    toast.success('Standard task notes filled for this visit');
  };

  const handleQuickConcern = (
    clientName: string,
    category: ConcernRecord['category'] = 'safeguarding',
    severity: ConcernRecord['severity'] = 'high'
  ) => {
    setConcernForm({
      clientName,
      category,
      severity,
      details: category === 'safeguarding'
        ? 'Safeguarding concern requires coordinator review.'
        : 'Urgent concern requires coordinator review.',
      actionTaken: 'Reported from carer quick action. Awaiting coordinator instruction.',
    });
    setActiveTab('concerns');
    toast('Concern form prepared. Review details and save.');
  };

  const handleGenerateCareNote = async (visit: VisitData, visitIndex: number) => {
    setIsGeneratingCareNote(true);
    try {
      const response = await fetch('/api/ai/admin-assistant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role: 'CARER',
          page: 'caregiver',
          question: 'Draft a clear, professional UK home care visit note and handover summary for the carer. Use only the context. Do not invent clinical facts. Keep it ready for review before saving.',
          context: {
            visit,
            taskEvidence: taskRecords.filter((record) => record.visitIndex === visitIndex),
            concerns: concerns.filter((concern) => concern.clientName === visit.clientName),
            pendingTasks: getPendingVisitTasks(visit, visitIndex, taskRecords),
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`AI service returned ${response.status}`);
      }

      const payload = (await response.json()) as { text?: string };
      const note = payload.text?.trim() || defaultCareNote(visit, taskRecords, concerns);
      setCareNoteDrafts((current) => ({ ...current, [visit.id]: note }));
      toast.success('AI care note drafted for review');
    } catch {
      setCareNoteDrafts((current) => ({
        ...current,
        [visit.id]: defaultCareNote(visit, taskRecords, concerns),
      }));
      toast.error('AI was unavailable. A standard handover note was created.');
    } finally {
      setIsGeneratingCareNote(false);
    }
  };

  const handleStartDictation = (visit: VisitData) => {
    const SpeechRecognitionConstructor = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognitionConstructor) {
      toast.error('Voice dictation is not supported in this browser.');
      return;
    }

    const recognition = new SpeechRecognitionConstructor();
    recognition.lang = 'en-GB';
    recognition.interimResults = false;
    recognition.continuous = false;

    recognition.onstart = () => setIsDictating(true);
    recognition.onend = () => setIsDictating(false);
    recognition.onerror = () => {
      setIsDictating(false);
      toast.error('Voice dictation stopped. Check microphone permission.');
    };
    recognition.onresult = (event: any) => {
      const transcript = Array.from(event.results ?? [])
        .map((result: any) => result?.[0]?.transcript ?? '')
        .join(' ')
        .trim();

      if (!transcript) return;

      setCareNoteDrafts((current) => ({
        ...current,
        [visit.id]: [current[visit.id], transcript].filter(Boolean).join('\n'),
      }));
      toast.success('Voice note added');
    };

    recognition.start();
  };

  const handleSaveCareNote = (visit: VisitData) => {
    const note = (careNoteDrafts[visit.id] ?? '').trim();
    if (!note) {
      toast.error('Write, dictate or generate a care note before saving.');
      return;
    }

    const record: CareNoteRecord = {
      id: createId('care-note'),
      visitId: visit.id,
      clientName: visit.clientName,
      visitType: visit.visitType,
      note,
      recordedAt: new Date().toISOString(),
      synced: connectionStatus.isConnected,
    };
    const nextNotes = [record, ...careNotes].slice(0, 100);
    setCareNotes(nextNotes);
    setLocalStorage(CARE_NOTES_KEY, nextNotes, CACHE_EXPIRATION.ONE_DAY);
    queueOfflineAction({
      type: 'care-note',
      label: `Care note saved for ${visit.clientName}`,
      payload: record,
    });
    toast.success('Care note saved and queued for sync');
  };

  const handleConcernSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!concernForm.details.trim()) return;

    const record: ConcernRecord = {
      id: createId('concern'),
      ...concernForm,
      details: concernForm.details.trim(),
      actionTaken: concernForm.actionTaken.trim() || 'Recorded for coordinator review.',
      recordedAt: new Date().toISOString(),
      synced: connectionStatus.isConnected,
    };
    const nextConcerns = [record, ...concerns].slice(0, 100);
    setConcerns(nextConcerns);
    setLocalStorage(CACHE_KEYS.CAREGIVER_CONCERNS, nextConcerns, CACHE_EXPIRATION.ONE_DAY);
    queueOfflineAction({
      type: 'concern',
      label: `Concern recorded for ${record.clientName}`,
      payload: record,
    });
    setConcernForm({
      clientName: visits[0]?.clientName ?? 'Margaret Williams',
      category: 'wellbeing',
      severity: 'medium',
      details: '',
      actionTaken: '',
    });
  };

  const handleSubmitTimesheet = () => {
    const nextTimesheets = timesheets.map((timesheet, index) =>
      index === 0 ? { ...timesheet, status: 'submitted' as const } : timesheet
    );
    setTimesheets(nextTimesheets);
    setLocalStorage(CACHE_KEYS.CAREGIVER_DASHBOARD_TIMESHEETS, nextTimesheets, CACHE_EXPIRATION.FIFTEEN_MINUTES);
    setTimesheetsLastUpdated(new Date());
    const currentWeek = nextTimesheets[0];
    const profile = getCaregiverPayrollProfile(user?.email, user?.name);
    if (currentWeek) {
      pushPayrollEntry({
        caregiverId: profile.id,
        date: getWeekStart(todayIso()),
        hours: currentWeek.hours,
        notes: 'Submitted current week timesheet from carer portal',
        source: 'carer-app',
        status: currentWeek.status,
        recordedBy: profile.name,
      });
    }
    queueOfflineAction({
      type: 'timesheet',
      label: 'Current week timesheet submitted',
      payload: nextTimesheets[0],
    });
  };

  const handleMarkQueueSynced = () => {
    persistOfflineQueue([]);
    const syncedTaskRecords = taskRecords.map((record) => ({ ...record, synced: true }));
    const syncedConcerns = concerns.map((record) => ({ ...record, synced: true }));
    setTaskRecords(syncedTaskRecords);
    setConcerns(syncedConcerns);
    setLocalStorage(CACHE_KEYS.CAREGIVER_TASK_RECORDS, syncedTaskRecords, CACHE_EXPIRATION.ONE_DAY);
    setLocalStorage(CACHE_KEYS.CAREGIVER_CONCERNS, syncedConcerns, CACHE_EXPIRATION.ONE_DAY);
  };

  return (
    <div className="space-y-6">
      {/* Network Status Banner */}
      <NetworkStatusBanner />

      {/* Connection Status Indicator */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Welcome, {user?.name ? user.name.split(' ')[0] : 'Caregiver'}!
          </h1>
          <p className="mt-1 text-gray-600">
            Here's your schedule for today
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          {connectionStatus.isConnected ? (
            <>
              <Wifi className="h-4 w-4 text-green-600" />
              <span className="text-green-700 font-medium">Online</span>
            </>
          ) : (
            <>
              <WifiOff className="h-4 w-4 text-amber-600" />
              <span className="text-amber-700 font-medium">Offline</span>
            </>
          )}
        </div>
      </div>

      {/* Stale Data Warning */}
      <StaleDataWarning
        lastUpdated={statsLastUpdated}
        onRefresh={async () => refreshAll()}
        isRefreshing={isRefreshingStats}
      />

      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
        <div className="flex items-start gap-3">
          <Shield className="mt-0.5 h-5 w-5 text-emerald-700" />
          <div>
            <h2 className="font-semibold text-emerald-950">Work verification control</h2>
            <p className="mt-1 text-sm leading-6 text-emerald-900">
              Hours are locked to scheduled visits only. A carer must check in from a visit in today's schedule and check out before payroll hours are saved for administrator approval.
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-3">
            <MapPin className="mt-0.5 h-5 w-5 text-blue-700" />
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="font-semibold text-blue-950">Browser GPS for carer control</h2>
                <span className={`rounded-full px-2 py-1 text-xs font-bold ${gpsPermissionClasses(gpsPermission)}`}>
                  {gpsPermissionText(gpsPermission)}
                </span>
              </div>
              <p className="mt-1 text-sm leading-6 text-blue-900">
                The browser must allow Location before check-in. On HTTPS and localhost, the app asks for GPS permission, verifies the carer is at the client address and blocks hours when GPS is refused.
              </p>
              {lastGpsFix && (
                <p className="mt-1 text-xs font-semibold text-blue-800">
                  Last GPS fix: {new Date(lastGpsFix.checkedAt).toLocaleTimeString('en-GB')} · accuracy {Math.round(lastGpsFix.accuracy ?? 0)}m
                </p>
              )}
            </div>
          </div>
          <Button
            variant={gpsPermission === 'granted' ? 'secondary' : 'primary'}
            onClick={requestGpsAccess}
          >
            {gpsPermission === 'granted' ? 'Test GPS again' : 'Enable GPS'}
          </Button>
        </div>
      </div>

      {activeVisit && (
        <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-[#6c3a80]">Carer quick workflow</p>
              <h2 className="mt-1 text-xl font-bold text-slate-950">Do this visit without searching menus</h2>
              <p className="mt-1 text-sm leading-6 text-slate-600">
                Current focus: {activeVisit.clientName} - {activeVisit.visitType} at {activeVisit.time}. Complete the checklist, record a note, then check out.
              </p>
            </div>
            <span className={`rounded-full px-3 py-1 text-xs font-bold ${
              activePendingTasks.length === 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
            }`}>
              {activeCompletedTaskCount}/{activeVisit.tasks.length} tasks recorded
            </span>
          </div>

          <div className="mt-4 grid gap-4 xl:grid-cols-[0.95fr_1.05fr]">
            <div className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => openDirections(activeVisit)}
                  className="flex min-h-24 items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 text-left transition hover:bg-blue-100"
                >
                  <Navigation className="h-6 w-6 text-blue-700" />
                  <span>
                    <span className="block font-bold text-blue-950">Directions</span>
                    <span className="text-sm text-blue-800">Open route to client</span>
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => callClient(activeVisit)}
                  className="flex min-h-24 items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-left transition hover:bg-emerald-100"
                >
                  <Phone className="h-6 w-6 text-emerald-700" />
                  <span>
                    <span className="block font-bold text-emerald-950">Call client</span>
                    <span className="text-sm text-emerald-800">{activeVisit.clientPhone ?? 'No number'}</span>
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => viewVisitDetails(activeVisit)}
                  className="flex min-h-24 items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 text-left transition hover:bg-slate-100"
                >
                  <ClipboardList className="h-6 w-6 text-slate-700" />
                  <span>
                    <span className="block font-bold text-slate-950">View details</span>
                    <span className="text-sm text-slate-600">Tasks, address, payroll status</span>
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => handleQuickConcern(activeVisit.clientName)}
                  className="flex min-h-24 items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-left transition hover:bg-red-100"
                >
                  <MessageSquareWarning className="h-6 w-6 text-red-700" />
                  <span>
                    <span className="block font-bold text-red-950">Concern</span>
                    <span className="text-sm text-red-800">Safeguarding or urgent issue</span>
                  </span>
                </button>
              </div>

              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h3 className="font-bold text-slate-950">Automatic visit checklist</h3>
                    <p className="mt-1 text-sm text-slate-600">
                      Check-out is blocked until every task is completed or marked not completed.
                    </p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => handleFillStandardTaskNotes(activeVisitIndex)}>
                    <Wand2 className="h-4 w-4" />
                    Auto-fill notes
                  </Button>
                </div>
                <div className="mt-3 space-y-2">
                  {activeVisit.tasks.map((task) => {
                    const record = getVisitTaskRecord(taskRecords, activeVisitIndex, task);
                    return (
                      <div key={task} className="flex flex-col gap-2 rounded-md bg-white p-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-start gap-2">
                          <CheckCircle className={`mt-0.5 h-4 w-4 ${record ? 'text-emerald-600' : 'text-slate-300'}`} />
                          <div>
                            <p className="text-sm font-semibold text-slate-900">{task}</p>
                            <p className="text-xs text-slate-500">{record ? `${record.status}${record.note ? ` - ${record.note}` : ''}` : 'Waiting for evidence'}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="primary" size="sm" onClick={() => handleTaskRecord(activeVisitIndex, task, 'completed')}>
                            Done
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleTaskRecord(activeVisitIndex, task, 'not-completed')}>
                            Not done
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="rounded-lg border border-[#6c3a80]/20 bg-[#6c3a80]/5 p-4">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h3 className="font-bold text-slate-950">AI care note and voice dictation</h3>
                    <p className="mt-1 text-sm text-slate-600">
                      Generate a draft from visit evidence, dictate extra details, then review before saving.
                    </p>
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleStartDictation(activeVisit)}
                      disabled={isDictating}
                    >
                      <Mic className="h-4 w-4" />
                      {isDictating ? 'Listening...' : 'Dictate'}
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => void handleGenerateCareNote(activeVisit, activeVisitIndex)}
                      disabled={isGeneratingCareNote}
                    >
                      <Sparkles className="h-4 w-4" />
                      {isGeneratingCareNote ? 'Generating...' : 'AI note'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSaveCareNote(activeVisit)}
                    >
                      <Send className="h-4 w-4" />
                      Save note
                    </Button>
                  </div>
                </div>
                <textarea
                  value={activeCareNote}
                  onChange={(event) =>
                    setCareNoteDrafts((current) => ({ ...current, [activeVisit.id]: event.target.value }))
                  }
                  rows={8}
                  placeholder="Care note draft appears here. You can dictate, type, or generate with AI."
                  className="mt-3 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#6c3a80] focus:ring-2 focus:ring-[#6c3a80]/20"
                />
              </div>

              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                <h3 className="font-bold text-emerald-950">Automatic handover summary</h3>
                <p className="mt-2 whitespace-pre-line text-sm leading-6 text-emerald-900">
                  {activeCareNote || activeHandoverSummary}
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      <Card>
        <CardHeader title="Carer App Controls" />
        <CardContent>
          <div className="space-y-5">
            <div className="flex gap-2 overflow-x-auto pb-1">
              {[
                { id: 'visits', label: 'Visits' },
                { id: 'tasks', label: 'Tasks' },
                { id: 'concerns', label: 'Concerns' },
                { id: 'timesheet', label: 'Timesheet' },
                { id: 'offline', label: `Offline Queue (${offlineQueue.length})` },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id as CarerTab)}
                  className={`whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold transition ${
                    activeTab === tab.id
                      ? 'bg-[#6c3a80] text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab === 'visits' && (
              <div className="grid gap-3 md:grid-cols-3">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center gap-2 text-slate-700">
                    <CheckCircle className="h-5 w-5 text-emerald-600" />
                    <p className="font-semibold">Check-in/out</p>
                  </div>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    Each visit card records check-in and check-out locally first, then queues the event for sync.
                  </p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center gap-2 text-slate-700">
                    <Navigation className="h-5 w-5 text-blue-600" />
                    <p className="font-semibold">Directions and calls</p>
                  </div>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    Carers can open map directions and call clients from mobile visit cards.
                  </p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center gap-2 text-slate-700">
                    <Database className="h-5 w-5 text-[#6c3a80]" />
                    <p className="font-semibold">Offline-first</p>
                  </div>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    Schedule, task evidence, concerns and timesheets are persisted in browser storage.
                  </p>
                </div>
              </div>
            )}

            {activeTab === 'tasks' && (
              <div className="space-y-4">
                {visits.map((visit, visitIndex) => (
                  <div key={visit.clientName} className="rounded-lg border border-slate-200 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <h3 className="font-semibold text-slate-950">{visit.clientName}</h3>
                        <p className="text-sm text-slate-600">{visit.visitType} - {visit.time}</p>
                      </div>
                      <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                        {visit.status}
                      </span>
                    </div>
                    <div className="mt-4 space-y-3">
                      {visit.tasks.map((task) => {
                        const noteKey = `${visitIndex}:${task}`;
                        return (
                          <div key={task} className="rounded-md bg-slate-50 p-3">
                            <p className="text-sm font-semibold text-slate-800">{task}</p>
                            <textarea
                              value={taskNotes[noteKey] ?? ''}
                              onChange={(event) => setTaskNotes({ ...taskNotes, [noteKey]: event.target.value })}
                              rows={2}
                              placeholder="Optional note for this task"
                              className="mt-2 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#6c3a80] focus:ring-2 focus:ring-[#6c3a80]/20"
                            />
                            <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => handleTaskRecord(visitIndex, task, 'completed')}
                              >
                                <ClipboardCheck className="h-4 w-4" />
                                Complete
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleTaskRecord(visitIndex, task, 'not-completed')}
                              >
                                Not completed
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}

                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <h3 className="font-semibold text-slate-950">Recent task records</h3>
                  <div className="mt-3 space-y-2">
                    {taskRecords.length === 0 ? (
                      <p className="text-sm text-slate-500">No task evidence recorded yet.</p>
                    ) : (
                      taskRecords.slice(0, 5).map((record) => (
                        <div key={record.id} className="flex items-start justify-between gap-3 rounded-md bg-slate-50 p-3 text-sm">
                          <div>
                            <p className="font-medium text-slate-900">{record.clientName} - {record.task}</p>
                            <p className="text-slate-600">{record.status} {record.note ? `- ${record.note}` : ''}</p>
                          </div>
                          <span className={`rounded-full px-2 py-1 text-xs font-semibold ${record.synced ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                            {record.synced ? 'Synced' : 'Queued'}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'concerns' && (
              <div className="grid gap-5 lg:grid-cols-[0.9fr_1.1fr]">
                <form onSubmit={handleConcernSubmit} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center gap-2">
                    <MessageSquareWarning className="h-5 w-5 text-amber-600" />
                    <h3 className="font-semibold text-slate-950">Record concern</h3>
                  </div>
                  <div className="mt-4 space-y-3">
                    <select
                      value={concernForm.clientName}
                      onChange={(event) => setConcernForm({ ...concernForm, clientName: event.target.value })}
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                    >
                      {visits.map((visit) => (
                        <option key={visit.clientName} value={visit.clientName}>{visit.clientName}</option>
                      ))}
                    </select>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <select
                        value={concernForm.category}
                        onChange={(event) => setConcernForm({ ...concernForm, category: event.target.value as ConcernRecord['category'] })}
                        className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                      >
                        <option value="wellbeing">Wellbeing</option>
                        <option value="medication">Medication</option>
                        <option value="safeguarding">Safeguarding</option>
                        <option value="environment">Environment</option>
                        <option value="other">Other</option>
                      </select>
                      <select
                        value={concernForm.severity}
                        onChange={(event) => setConcernForm({ ...concernForm, severity: event.target.value as ConcernRecord['severity'] })}
                        className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                    </div>
                    <textarea
                      value={concernForm.details}
                      onChange={(event) => setConcernForm({ ...concernForm, details: event.target.value })}
                      rows={4}
                      placeholder="What changed, what did you observe?"
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                    />
                    <textarea
                      value={concernForm.actionTaken}
                      onChange={(event) => setConcernForm({ ...concernForm, actionTaken: event.target.value })}
                      rows={3}
                      placeholder="Action taken or escalation"
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                    />
                    <Button variant="primary" size="sm" className="w-full justify-center">
                      <Send className="h-4 w-4" />
                      Save concern
                    </Button>
                  </div>
                </form>

                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <h3 className="font-semibold text-slate-950">Concern log</h3>
                  <div className="mt-3 space-y-2">
                    {concerns.length === 0 ? (
                      <p className="text-sm text-slate-500">No concerns recorded.</p>
                    ) : (
                      concerns.slice(0, 6).map((concern) => (
                        <div key={concern.id} className="rounded-md bg-slate-50 p-3 text-sm">
                          <div className="flex items-center justify-between gap-3">
                            <p className="font-medium text-slate-900">{concern.clientName}</p>
                            <span className={`rounded-full px-2 py-1 text-xs font-semibold ${concern.severity === 'high' ? 'bg-red-100 text-red-800' : concern.severity === 'medium' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-700'}`}>
                              {concern.severity}
                            </span>
                          </div>
                          <p className="mt-1 text-slate-600">{concern.category}: {concern.details}</p>
                          <p className="mt-1 text-slate-500">Action: {concern.actionTaken}</p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'timesheet' && (
              <div className="space-y-4">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h3 className="font-semibold text-slate-950">Current week timesheet</h3>
                      <p className="mt-1 text-sm text-slate-600">
                        {timesheets[0]?.hours ?? 0} hours recorded. Status: {timesheets[0]?.status ?? 'draft'}.
                      </p>
                    </div>
                    <Button variant="primary" size="sm" onClick={handleSubmitTimesheet}>
                      Submit timesheet
                    </Button>
                  </div>
                </div>

                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                      <h3 className="font-semibold text-emerald-950">My worked hours</h3>
                      <p className="mt-1 text-sm leading-6 text-emerald-900">
                        These hours are saved to the payroll database used by the administrator for weekly and monthly salary PDFs.
                      </p>
                    </div>
                    <Button variant="outline" size="sm" onClick={refreshPayrollEntries}>
                      Refresh hours
                    </Button>
                  </div>
                  <div className="mt-4 grid gap-3 md:grid-cols-3">
                    <div className="rounded-lg bg-white p-4 shadow-sm">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Today</p>
                      <p className="mt-2 text-3xl font-bold text-slate-950">{formatHours(todayHours)}</p>
                      <p className="mt-1 text-sm text-slate-600">hours worked</p>
                    </div>
                    <div className="rounded-lg bg-white p-4 shadow-sm">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">This Week</p>
                      <p className="mt-2 text-3xl font-bold text-slate-950">{formatHours(currentWeekHours)}</p>
                      <p className="mt-1 text-sm text-slate-600">hours worked</p>
                    </div>
                    <div className="rounded-lg bg-white p-4 shadow-sm">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">This Month</p>
                      <p className="mt-2 text-3xl font-bold text-slate-950">{formatHours(currentMonthHours)}</p>
                      <p className="mt-1 text-sm text-slate-600">hours worked</p>
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                      <h3 className="font-semibold text-slate-950">Hours by day, week, and month</h3>
                      <p className="mt-1 text-sm text-slate-600">
                        Selected period: {periodLabel(hoursPeriod, hoursDate)} - {formatHours(selectedPeriodHours)} hours.
                      </p>
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <div className="inline-flex rounded-md border border-slate-300 bg-white p-1">
                        {(['day', 'week', 'month'] as HoursPeriod[]).map((period) => (
                          <button
                            key={period}
                            type="button"
                            onClick={() => setHoursPeriod(period)}
                            className={`rounded px-3 py-1.5 text-sm font-semibold capitalize transition ${
                              hoursPeriod === period ? 'bg-[#6c3a80] text-white' : 'text-slate-600 hover:bg-slate-100'
                            }`}
                          >
                            {period}
                          </button>
                        ))}
                      </div>
                      <input
                        type="date"
                        value={hoursDate}
                        onChange={(event) => setHoursDate(event.target.value)}
                        className="rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-950"
                      />
                    </div>
                  </div>

                  <div className="mt-4 overflow-x-auto rounded-lg border border-slate-200">
                    <table className="min-w-full divide-y divide-slate-200 text-sm">
                      <thead className="bg-slate-50">
                        <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                          <th className="px-4 py-3">Date</th>
                          <th className="px-4 py-3">Client</th>
                          <th className="px-4 py-3">Hours</th>
                          <th className="px-4 py-3">Status</th>
                          <th className="px-4 py-3">Notes</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white">
                        {selectedPeriodEntries.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="px-4 py-6 text-center text-slate-500">
                              No hours recorded for this period yet.
                            </td>
                          </tr>
                        ) : (
                          selectedPeriodEntries.map((entry) => (
                            <tr key={entry.id}>
                              <td className="px-4 py-3 text-slate-700">{formatDate(entry.date)}</td>
                              <td className="px-4 py-3 font-semibold text-slate-950">{entry.clientName ?? 'Weekly timesheet'}</td>
                              <td className="px-4 py-3 text-slate-700">{formatHours(entry.hours)}</td>
                              <td className="px-4 py-3">
                                <span className="rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-800">
                                  {entry.status ?? 'draft'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-slate-600">{entry.notes}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'offline' && (
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h3 className="font-semibold text-slate-950">Offline sync queue</h3>
                    <p className="mt-1 text-sm text-slate-600">
                      {offlineQueue.length} locally recorded action{offlineQueue.length === 1 ? '' : 's'} waiting for production API sync.
                    </p>
                  </div>
                  <Button variant="outline" size="sm" onClick={handleMarkQueueSynced}>
                    Mark preview queue synced
                  </Button>
                </div>
                <div className="mt-4 space-y-2">
                  {offlineQueue.length === 0 ? (
                    <p className="text-sm text-slate-500">No pending local actions.</p>
                  ) : (
                    offlineQueue.map((item) => (
                      <div key={item.id} className="rounded-md bg-slate-50 p-3 text-sm">
                        <p className="font-medium text-slate-900">{item.label}</p>
                        <p className="text-slate-500">{new Date(item.createdAt).toLocaleString()} - {item.type}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900">Overview</h2>
          <DataFreshness
            lastUpdated={statsLastUpdated}
            onRefresh={async () => fetchStats()}
            isRefreshing={isRefreshingStats}
            compact
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          <Card padding="md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Today's Visits</p>
                <p className="mt-2 text-3xl font-bold text-gray-900">{stats.todayVisitsCount}</p>
                <p className="mt-1 text-sm text-gray-600">{stats.todayHoursScheduled} hours scheduled</p>
              </div>
              <Calendar className="h-6 w-6 text-primary-600" />
            </div>
          </Card>

          <Card padding="md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">This Week</p>
                <p className="mt-2 text-3xl font-bold text-gray-900">{stats.weekHoursWorked}</p>
                <p className="mt-1 text-sm text-gray-600">hours worked</p>
              </div>
              <Clock className="h-6 w-6 text-green-600" />
            </div>
          </Card>

          <Card padding="md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Clients</p>
                <p className="mt-2 text-3xl font-bold text-gray-900">{stats.activeClientsCount}</p>
                <p className="mt-1 text-sm text-gray-600">on your schedule</p>
              </div>
              <Users className="h-6 w-6 text-blue-600" />
            </div>
          </Card>
        </div>
      </div>

      {/* Credential Alerts */}
      {credentials.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className={`${isMobile ? 'h-6 w-6' : 'h-5 w-5'} text-yellow-600`} />
              <h2 className={`${isMobile ? 'text-xl' : 'text-lg'} font-semibold text-gray-900`}>
                Training & Credential Reminders
              </h2>
            </div>
            <DataFreshness
              lastUpdated={credentialsLastUpdated}
              onRefresh={async () => fetchCredentials()}
              isRefreshing={isRefreshingCredentials}
              compact
            />
          </div>
          <div className="grid grid-cols-1 gap-3">
            {credentials.map((credential, index) => (
              <CredentialAlert
                key={index}
                {...credential}
                onClick={handleNavigateToCredentials}
              />
            ))}
          </div>
        </div>
      )}

      {/* Today's Schedule */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className={`${isMobile ? 'text-xl' : 'text-lg'} font-semibold text-gray-900`}>Today's Schedule</h2>
          <div className="flex items-center gap-3">
            <DataFreshness
              lastUpdated={visitsLastUpdated}
              onRefresh={async () => fetchVisits()}
              isRefreshing={isRefreshingVisits}
              compact
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleNavigateToSchedule}
            >
              View Full Schedule
            </Button>
          </div>
        </div>
        <div className="space-y-4">
          {visits.length > 0 ? (
            visits.map((visit, index) => (
              <VisitCard
                key={index}
                {...visit}
                onNavigate={() => openDirections(visit)}
                onCheckIn={async () => handleCheckIn(index)}
                onCheckOut={async () => handleCheckOut(index)}
                onViewDetails={() => viewVisitDetails(visit)}
                onCall={visit.clientPhone ? () => callClient(visit) : undefined}
              />
            ))
          ) : (
            <Card>
              <CardContent>
                <EmptyState
                  title="No visits scheduled today"
                  description="Enjoy your day off! Check back tomorrow for your schedule."
                  icon={<Calendar className="h-12 w-12" />}
                />
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {selectedVisit && (
        <Card>
          <CardContent>
            <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-950">Visit details - {selectedVisit.clientName}</h2>
                <p className="mt-1 text-sm text-slate-600">
                  {selectedVisit.visitType} scheduled {selectedVisit.scheduledDate === todayIso() ? 'today' : formatDate(selectedVisit.scheduledDate)} at {selectedVisit.time}
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setSelectedVisit(null)}>
                Close
              </Button>
            </div>
            <div className="grid gap-4 lg:grid-cols-3">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Schedule</p>
                <p className="mt-2 font-semibold text-slate-950">{selectedVisit.time}</p>
                <p className="text-sm text-slate-600">{selectedVisit.duration}</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Address</p>
                <p className="mt-2 text-sm font-semibold text-slate-950">{selectedVisit.address}</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Payroll status</p>
                <p className="mt-2 font-semibold capitalize text-emerald-800">
                  {(selectedVisit.verificationStatus ?? 'scheduled').replace(/-/g, ' ')}
                </p>
                <p className="mt-1 text-sm text-slate-600">
                  GPS: {selectedVisit.geofenceStatus ?? 'waiting for check-in'}
                </p>
                {selectedVisit.verificationExceptions && selectedVisit.verificationExceptions.length > 0 && (
                  <p className="mt-2 text-sm font-semibold text-amber-800">
                    {selectedVisit.verificationExceptions.join('; ')}
                  </p>
                )}
              </div>
            </div>
            <div className="mt-4 rounded-lg border border-slate-200 p-4">
              <p className="font-semibold text-slate-950">Required tasks</p>
              <ul className="mt-3 space-y-2 text-sm text-slate-700">
                {selectedVisit.tasks.map((task) => (
                  <li key={task} className="flex items-start gap-2">
                    <CheckCircle className="mt-0.5 h-4 w-4 text-emerald-600" />
                    <span>{task}</span>
                  </li>
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Timesheet Status */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className={`${isMobile ? 'text-xl' : 'text-lg'} font-semibold text-gray-900`}>Timesheet Status</h2>
          <div className="flex items-center gap-3">
            <DataFreshness
              lastUpdated={timesheetsLastUpdated}
              onRefresh={async () => fetchTimesheets()}
              isRefreshing={isRefreshingTimesheets}
              compact
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleNavigateToTimeTracking}
            >
              View All
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4">
          {timesheets.map((timesheet, index) => (
            <TimesheetStatus
              key={index}
              {...timesheet}
              onClick={() => { 
                const period = index === 0 ? 'current' : index === 1 ? 'last' : String(index);
                navigate(`/time-tracking?period=${period}`);
              }}
            />
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader title="Quick Actions" />
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Button
              variant="outline"
              className="justify-start"
              onClick={handleNavigateToTimeTracking}
            >
              <Clock className="h-5 w-5 mr-2" />
              Submit Timesheet
            </Button>
            <Button
              variant="outline"
              className="justify-start"
              onClick={handleNavigateToSchedule}
            >
              <Calendar className="h-5 w-5 mr-2" />
              View Schedule
            </Button>
            <Button
              variant="outline"
              className="justify-start"
              onClick={handleNavigateToCredentials}
            >
              <Award className="h-5 w-5 mr-2" />
              Update Credentials
            </Button>
            <Button
              variant="outline"
              className="justify-start"
              onClick={handleNavigateToTraining}
            >
              <FileText className="h-5 w-5 mr-2" />
              Training Materials
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
